import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import styles from './GrapeBoard.module.scss';
import grapeCell from '../../assets/images/grapecell.png';
import selectedGrapeCell from '../../assets/images/selectedgrapecell.png';
import { useLanguage } from '../../contexts/LanguageContext';
import popSound from '../../assets/sounds/pop.mp3';

const GAME_TIME = 120; // 2분 = 120초
const MOVE_TIME = 15; // 정답 입력 제한 시간 15초

// 난이도 설정 수정
const DIFFICULTY_SETTINGS = {
  free: { moveTime: 15, targetScore: 0 },
  1: { moveTime: 20, targetScore: 20 },
  2: { moveTime: 20, targetScore: 30 },
  3: { moveTime: 15, targetScore: 40 },
  4: { moveTime: 15, targetScore: 50 },
  5: { moveTime: 10, targetScore: 60 },
  6: { moveTime: 10, targetScore: 70 },
  7: { moveTime: 10, targetScore: 80 },
  8: { moveTime: 7, targetScore: 90 },
  9: { moveTime: 7, targetScore: 100 },
  10: { moveTime: 5, targetScore: 110 }  // 난이도 10 추가
};

const GrapeBoard = forwardRef((props, ref) => {
  const ROWS = 15;  // 세로 15줄로 수정
  const COLS = 8;   // 가로 8줄 유지
  const [board, setBoard] = useState([]);
  const [firstCell, setFirstCell] = useState(null);
  const [selectedCells, setSelectedCells] = useState(null);
  const [score, setScore] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const { t } = useLanguage();
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const popAudio = new Audio(popSound);
  const respawnTimersRef = useRef({});
  const [moveTimer, setMoveTimer] = useState(DIFFICULTY_SETTINGS.free.moveTime);
  const [difficulty, setDifficulty] = useState('free');
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [isSoundMuted, setIsSoundMuted] = useState(false);

  // 타이머 ref 추가
  const gameTimerRef = useRef(null);
  const moveTimerRef = useRef(null);

  // ref를 통해 메서드 노출
  useImperativeHandle(ref, () => ({
    handleRestart,
    isGameStarted
  }));

  // 초기 보드 생성 함수 수정
  const initializeBoard = () => {
    let numbers = [];
    for (let i = 1; i <= 9; i++) {
      const count = i <= 3 ? 14 : 13;
      numbers = numbers.concat(Array(count).fill(i));
    }
    
    for (let i = numbers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
    }

    const newBoard = [];
    for (let i = 0; i < ROWS; i++) {
      newBoard[i] = [];
      for (let j = 0; j < COLS; j++) {
        const index = i * COLS + j;
        // 일반 숫자로 초기화
        newBoard[i][j] = numbers[index];
      }
    }
    setBoard(newBoard);
  };

  useEffect(() => {
    initializeBoard();
  }, []);

  const playPopSound = () => {
    if (!isSoundMuted) {
      popAudio.currentTime = 0;
      popAudio.play().catch(err => console.log('효과음 재생 실패:', err));
    }
  };

  const handleCellClick = (row, col) => {
    if (!isGameStarted || isGameOver) {
      return;
    }

    if (!firstCell) {
      setFirstCell({ row, col });
      setSelectedCells({ startRow: row, endRow: row, startCol: col, endCol: col });
      setIsSelecting(true);
    } else {
      // 두 번째 선택
      const startRow = Math.min(firstCell.row, row);
      const endRow = Math.max(firstCell.row, row);
      const startCol = Math.min(firstCell.col, col);
      const endCol = Math.max(firstCell.col, col);

      setSelectedCells({ startRow, endRow, startCol, endCol });
      setIsSelecting(false);
      checkSelection(startRow, endRow, startCol, endCol);
      setFirstCell(null);
    }
  };

  const getCellContent = (row, col, cell) => {
    if (cell === 0) return null;

    // 셀 값 가져오기
    const cellValue = typeof cell === 'object' ? cell.value : cell;
    let imageSource = grapeCell;
    
    if (firstCell && firstCell.row === row && firstCell.col === col) {
      imageSource = selectedGrapeCell;
    } else if (selectedCells && 
      row >= selectedCells.startRow && 
      row <= selectedCells.endRow && 
      col >= selectedCells.startCol && 
      col <= selectedCells.endCol
    ) {
      imageSource = selectedGrapeCell;
    }

    return (
      <>
        <span className={styles.number}>{cellValue}</span>
        <img src={imageSource} alt="grape" className={styles.grapeImage} />
      </>
    );
  };

  // 게임 재시작 시 모든 타이머 초기화
  const clearAllTimers = () => {
    // 게임 타이머 정리
    if (gameTimerRef.current) {
      clearInterval(gameTimerRef.current);
      gameTimerRef.current = null;
    }
    
    // 이동 타이머 정리
    if (moveTimerRef.current) {
      clearInterval(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    
    // 리스폰 타이머들 정리
    Object.values(respawnTimersRef.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    respawnTimersRef.current = {};
  };

  // 게임 시작/재시작 핸들러 수정
  const startGame = () => {
    // 기존 타이머들 정리
    clearAllTimers();
    
    setIsGameStarted(true);
    setTimeLeft(GAME_TIME);
    setMoveTimer(DIFFICULTY_SETTINGS[difficulty].moveTime);
    setScore(0);
    initializeBoard();

    // 게임 타이머 시작
    gameTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearAllTimers();
          setIsGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // handleRestart를 handleGameStart로 연결
  const handleRestart = () => {
    clearAllTimers();
    setIsGameStarted(false);
    setIsGameOver(false);
    setShowVictoryModal(false);
    setScore(0);
    setSelectedCells(null);
    setFirstCell(null);
    initializeBoard();
  };

  const checkSelection = (startRow, endRow, startCol, endCol) => {
    let sum = 0;
    let selectedCells = [];

    for (let i = startRow; i <= endRow; i++) {
      for (let j = startCol; j <= endCol; j++) {
        const cell = board[i][j];
        sum += (typeof cell === 'number' ? cell : (cell?.value || 0));
        selectedCells.push({ row: i, col: j });
      }
    }

    if (sum === 10) {
      // 정답일 때만 타이머 리셋
      setMoveTimer(DIFFICULTY_SETTINGS[difficulty].moveTime);
      const newBoard = JSON.parse(JSON.stringify(board));
      
      selectedCells.forEach(({ row, col }) => {
        newBoard[row][col] = 0;
        
        const timerKey = `${row}-${col}`;
        
        if (respawnTimersRef.current[timerKey]) {
          clearTimeout(respawnTimersRef.current[timerKey]);
        }

        const respawnTime = 10000 + Math.floor(Math.random() * 100000);
        const timerId = setTimeout(() => {
          console.log(`Respawning cell at ${row},${col}`);
          const newNumber = Math.floor(Math.random() * 9) + 1;
          
          setBoard(prevBoard => {
            const nextBoard = JSON.parse(JSON.stringify(prevBoard));
            if (nextBoard[row][col] === 0) {
              nextBoard[row][col] = { value: newNumber, isNew: true };
            }
            return nextBoard;
          });

          setTimeout(() => {
            setBoard(prevBoard => {
              const nextBoard = JSON.parse(JSON.stringify(prevBoard));
              if (nextBoard[row][col]?.isNew) {
                nextBoard[row][col] = newNumber;
              }
              return nextBoard;
            });
          }, 500);

          // 타이머 완료 후 ref에서 제거
          delete respawnTimersRef.current[timerKey];
        }, respawnTime);

        // 새 타이머를 ref에 저장
        respawnTimersRef.current[timerKey] = timerId;
      });
      
      setBoard(newBoard);
      setScore(score + selectedCells.length);
      playPopSound();
    }
    
    setTimeout(() => {
      setSelectedCells(null);
    }, 300);
  };

  const getCellClassName = (row, col) => {
    let className = styles.cell;
    
    if (firstCell && firstCell.row === row && firstCell.col === col) {
      className += ' ' + styles.selected;
    }
    
    if (selectedCells) {
      const { startRow, endRow, startCol, endCol } = selectedCells;
      if (row >= startRow && row <= endRow && col >= startCol && col <= endCol) {
        className += ' ' + styles.selected;
      }
    }

    // 새로 생성된 셀인 경우 애니메이션 클래스 추가
    if (board[row][col]?.isNew) {
      className += ' ' + styles.newCell;
    }
    
    return className;
  };

  // 입력 시간 타이머 useEffect 수정
  useEffect(() => {
    let timer;
    if (isGameStarted && !isGameOver) {
      timer = setInterval(() => {
        setMoveTimer(prev => {
          if (prev <= 1) {
            setIsGameOver(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isGameStarted, isGameOver]);

  // 선택 취소 시 타이머 리셋 로직 수정
  const resetSelection = () => {
    setFirstCell(null);
    setSelectedCells(null);
    // 선택 취소 시 타이머 리셋
    setMoveTimer(DIFFICULTY_SETTINGS[difficulty].moveTime);
  };

  // 난이도 변경 핸들러
  const handleDifficultyChange = (e) => {
    if (isGameStarted) {
      return;
    }
    const newDifficulty = e.target.value;
    setDifficulty(newDifficulty);
    setMoveTimer(DIFFICULTY_SETTINGS[newDifficulty].moveTime);
  };

  // 점수 체크 로직 수정
  useEffect(() => {
    if (isGameStarted && !isGameOver) {
      const settings = DIFFICULTY_SETTINGS[difficulty];
      if (settings.targetScore > 0 && score >= settings.targetScore) {
        setShowVictoryModal(true);
        setIsGameOver(true);
      }
    }
  }, [score, difficulty, isGameStarted, isGameOver]);

  // 다음 단계로 이동
  const handleNextLevel = () => {
    const currentLevel = parseInt(difficulty);
    if (currentLevel < 10) {
      setDifficulty((currentLevel + 1).toString());
      setShowVictoryModal(false);
      handleRestart();
    }
  };

  return (
    <div className={styles.boardContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.difficultyContainer}>
          <select 
            value={difficulty}
            onChange={handleDifficultyChange}
            disabled={isGameStarted}
            className={styles.difficultySelect}
          >
            <option value="free">{t('games.grape.difficulty.free')}</option>
            {[1,2,3,4,5,6,7,8,9,10].map(level => (
              <option key={level} value={level.toString()}>
                {t('games.grape.difficulty.level')} {level}
              </option>
            ))}
          </select>
          {difficulty !== 'free' && (
            <div className={styles.targetInfo}>
              <span>{t('games.grape.difficulty.target')}: {DIFFICULTY_SETTINGS[difficulty].targetScore}</span>
              <span>{t('games.grape.difficulty.input')}: {DIFFICULTY_SETTINGS[difficulty].moveTime}s</span>
            </div>
          )}
        </div>
        <div className={styles.soundControl}>
          <span className={styles.soundLabel}>
            {t('games.common.sound')}
          </span>
          <button 
            className={styles.soundToggle}
            onClick={() => setIsSoundMuted(!isSoundMuted)}
          >
            {isSoundMuted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>

      <div className={styles.score}>{t('games.grape.score')}: {score}</div>

      <div className={styles.timerRow}>
        <div className={styles.gameTimeText}>
          {t('games.grape.timers.gameTime')} :
          <span>{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
        </div>
        <div className={styles.moveTimerWrapper}>
          <span className={styles.moveTimerLabel}>{t('games.grape.timers.moveTime')} :</span>
          <div className={styles.moveTimerContainer}>
            <div 
              className={styles.moveTimerBar}
              style={{ 
                width: `${(moveTimer / DIFFICULTY_SETTINGS[difficulty].moveTime) * 100}%`,
                backgroundColor: moveTimer <= 3 ? '#ff4444' : '#4A90E2'
              }}
            />
            <div className={styles.moveTimerText}>
              {moveTimer}s
            </div>
          </div>
        </div>
      </div>

      <div className={styles.board}>
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.row}>
            {row.map((cell, colIndex) => (
              <div
                key={colIndex}
                className={getCellClassName(rowIndex, colIndex)}
                onClick={() => handleCellClick(rowIndex, colIndex)}
              >
                {cell !== 0 && getCellContent(rowIndex, colIndex, cell)}
              </div>
            ))}
          </div>
        ))}
        {!isGameStarted && (
          <div className={styles.startOverlay}>
            <button 
              className={styles.startButton}
              onClick={startGame}
            >
              {t('games.grape.start')}
            </button>
          </div>
        )}
      </div>

      {/* 게임 오버 모달은 승리 모달이 없을 때만 표시 */}
      {isGameOver && !showVictoryModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t('games.grape.gameOver.title')}</h2>
            <p style={{ whiteSpace: 'pre-line' }}>
              {timeLeft === 0 
                ? t('games.grape.gameOver.timeUp') 
                : moveTimer === 0 
                  ? t('games.grape.gameOver.moveTimeUp')
                  : t('games.grape.gameOver.message')
              }
              {t('games.grape.gameOver.score').replace('%score%', score)}
            </p>
            <div className="modal-buttons">
              <button onClick={handleRestart}>
                {t('playAgain')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승리 모달 */}
      {showVictoryModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>축하합니다!</h2>
            <p>{difficulty}단계를 클리어했습니다!</p>
            <p>점수: {score}점</p>
            <div className="modal-buttons">
              <button onClick={handleRestart}>
                다시 시작
              </button>
              {difficulty !== '10' && (
                <button onClick={handleNextLevel}>
                  다음 단계
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 하단 타이머 UI */}
      <div className={`${styles.timerRow} ${styles.bottomTimerRow}`}>
        <div className={styles.gameTimeText}>
          {t('games.grape.timers.gameTime')} :
          <span>{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
        </div>
        <div className={styles.moveTimerWrapper}>
          <span className={styles.moveTimerLabel}>{t('games.grape.timers.moveTime')} :</span>
          <div className={styles.moveTimerContainer}>
            <div 
              className={styles.moveTimerBar}
              style={{ 
                width: `${(moveTimer / DIFFICULTY_SETTINGS[difficulty].moveTime) * 100}%`,
                backgroundColor: moveTimer <= 3 ? '#ff4444' : '#4A90E2'
              }}
            />
            <div className={styles.moveTimerText}>
              {moveTimer}s
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default GrapeBoard; 